import type { AuditLogRow, Lookups, RefCollector, RefSink, Summarizer } from "./index";
import {
  arr,
  asRecord,
  bool,
  buildSummary,
  entityLabelOf,
  fieldsFragment,
  formatIsoDay,
  isUuid,
  num,
  obj,
  plural,
  refFragment,
  resolvePerson,
  resolveTarget,
  str,
  translate,
  type Fragment,
  type MissingKind,
  type Resolution,
} from "./vocab";

// ---------------------------------------------------------------------------
// Vocabulary
// ---------------------------------------------------------------------------

/** legal_documents.slug, used only when the live title cannot be read (KTD7). */
export const LEGAL_SLUG_LABELS: Record<string, string> = {
  "terms-of-use": "Termos de Uso",
  "privacy-policy": "Política de Privacidade",
  "push-notifications": "Notificações",
  "affinity-profiling": "Perfil de Afinidade",
  "image-authorization": "Autorização de Imagem",
  "membership-contract": "Contrato de Associação",
  "payment-provider-choice": "Meio de Pagamento",
  "payment-backup-card": "Cartão Reserva",
  "recurring-charge-authorization": "Autorização de Cobrança Recorrente",
};

/** consent_records.consent_type of the legacy consent_granted / consent_revoked rows. */
export const CONSENT_TYPE_LABELS: Record<string, string> = {
  terms: "Termos de Uso",
  privacy_policy: "Política de Privacidade",
  push_notifications: "Notificações",
  affinity_profiling: "Perfil de Afinidade",
  payment_provider_choice: "Meio de Pagamento",
  payment_backup_card: "Cartão Reserva",
};

const DECISION_LABELS: Record<string, string> = {
  granted: "aceitou",
  revoked: "revogou",
};

const ROLE_LABELS: Record<string, string> = {
  member: "membro",
  prospect: "prospect",
  staff: "staff",
};

const STATUS_LABELS: Record<string, string> = {
  active: "ativo",
  inactive: "inativo",
  suspended: "suspenso",
  pending: "pendente",
};

const BLOCK_REASON_LABELS: Record<string, string> = {
  dunning: "inadimplência",
  dispute: "contestação de pagamento",
  missing_payment_method: "sem cartão cadastrado",
};

/** Mirrors the member-facing labels in apps/mobile/app/privacy/history.tsx. */
const VISIBILITY_FIELD_LABELS: Record<string, string> = {
  avatar: "Foto",
  company: "Empresa",
  job_title: "Cargo",
  sector: "Setor",
  city: "Cidade",
  bio: "Bio",
  interests: "Interesses",
  tier: "Plano",
  achievements: "Conquistas",
  groups: "Grupos",
  attended_events: "Eventos confirmados",
};

const CONTRACT_START_EFFECT_LABELS: Record<string, string> = {
  stamped: "“membro desde” registrado",
  cleared: "“membro desde” apagado",
};

const DEACTIVATE_REASON_LABELS: Record<string, string> = {
  contract_expired: "contrato vencido",
};

const DELETION_SUBSCRIPTIONS_LABELS: Record<string, string> = {
  "nothing-to-cancel": "nada a cancelar",
};

const EXPORT_ROLE_LABELS: Record<string, string> = {
  member: "pedido pelo próprio titular",
  staff: "pedido pelo staff",
};

const RETENTION: Record<string, { primary: string; why: string }> = {
  consent_ledger_retained: { primary: "Aceites de documentos", why: "guardados por obrigação legal" },
  terms_acceptances_retained: { primary: "Aceites de termos de atividades", why: "guardados por obrigação legal" },
  feed_posts_retained: { primary: "Posts do News", why: "mantidos com a autoria registrada" },
};

/**
 * Known machine-written prefixes of profile_lifecycle_mutation.reason. Anything
 * else is a hand-written note and is shown verbatim.
 */
const LIFECYCLE_REASONS: readonly (readonly [RegExp, string])[] = [
  [/^reconciler drift\b/i, "reconciliação automática"],
  [/^stripe invoice\.paid\b/i, "1ª fatura paga"],
  [/^\d+x first invoice paid\b/i, "1ª parcela paga"],
  [/^courtesy skipCard\b/i, "ativação sem cartão"],
  [/^local adhesion\b/i, "adesão presencial"],
  [/^zero-price activation\b/i, "ativação sem custo"],
  [/^stripe subscription\.deleted\b/i, "assinatura encerrada"],
  [/^cancel-subscription \(mode=immediate\)/i, "assinatura cancelada na hora"],
  [/^cancel-subscription\b/i, "assinatura cancelada"],
  [/^assign-staff-role\b/i, "papel de staff atribuído"],
  [/^revoke-staff-role\b/i, "papel de staff revogado"],
  [/^create_(member|staff)$/i, "cadastrado pelo staff"],
  [/^tenant provisioning\b/i, "primeiro admin do clube"],
];

const STAFF_ROLE_IN_REASON = /\brole=([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})\b/i;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const BRT_DAY = new Intl.DateTimeFormat("pt-BR", {
  timeZone: "America/Sao_Paulo",
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
});

/** A date-only value splits as a string (KTD11); a full timestamp is shown as the club's (BRT) day. */
function formatDay(value: unknown): string | null {
  if (typeof value !== "string") return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(value.trim())) return formatIsoDay(value.trim());
  const t = Date.parse(value);
  return Number.isNaN(t) ? formatIsoDay(value) : BRT_DAY.format(new Date(t));
}

function entityLabel(row: AuditLogRow): string {
  return entityLabelOf(row, row.action);
}

/** A person as the primary line; an id that was never recorded falls back to the entity label. */
function personPrimary(row: AuditLogRow, lookups: Lookups, id: unknown, kind: MissingKind = "profile"): Resolution | string {
  return isUuid(id) ? resolvePerson(lookups, id, kind) : entityLabel(row);
}

function tierFragment(lookups: Lookups, id: unknown): string | null {
  return refFragment(resolveTarget(lookups, "tier", id), (t) => `plano ${t}`);
}

/** `details.version` is a number in prod; a string is tolerated and shown as written. */
function versionFragment(details: unknown): string | null {
  const v = num(details, "version") ?? str(details, "version");
  return v === null ? null : `versão ${v}`;
}

function changesOf(details: unknown): Record<string, unknown> {
  return obj(details, "changes") ?? {};
}

function change(changes: Record<string, unknown>, key: string): { old: unknown; new: unknown } | null {
  const c = asRecord(changes[key]);
  if (!c) return null;
  const before = c.old ?? null;
  const after = c.new ?? null;
  return JSON.stringify(before) === JSON.stringify(after) ? null : { old: before, new: after };
}

function enumOrNone(map: Record<string, string>, value: unknown, none: string): string {
  return typeof value === "string" && value !== "" ? translate(map, value) : none;
}

function decodeLifecycleReason(reason: string): string {
  for (const [re, label] of LIFECYCLE_REASONS) if (re.test(reason.trim())) return label;
  return reason;
}

function staffRoleInReason(details: unknown): string | null {
  const reason = str(details, "reason");
  if (!reason || !/^assign-staff-role\b/i.test(reason.trim())) return null;
  return STAFF_ROLE_IN_REASON.exec(reason)?.[1] ?? null;
}

/** Document primary (KTD7): live title, else the slug name, else the raw slug. */
function documentPrimary(row: AuditLogRow, lookups: Lookups): Resolution | string {
  const docId = asRecord(row.details)?.document_id;
  const slug = str(row.details, "slug");
  const res = isUuid(docId) ? resolveTarget(lookups, "legal_document", docId) : null;
  if (res && (res.state === "ok" || res.state === "pending")) return res;
  if (slug) return translate(LEGAL_SLUG_LABELS, slug);
  return res ?? entityLabel(row);
}

function documentTarget(lookups: Lookups, id: unknown): Resolution | null {
  return isUuid(id) ? resolveTarget(lookups, "legal_document", id) : null;
}

// ---------------------------------------------------------------------------
// Summarizers
// ---------------------------------------------------------------------------

const consentLedger: Summarizer = (row, lookups) => {
  const d = row.details;
  const fallback = row.action === "consent_ledger_revoked" ? "revogou" : "aceitou";
  const decision = str(d, "decision");
  return buildSummary(documentPrimary(row, lookups), [
    versionFragment(d),
    decision ? translate(DECISION_LABELS, decision) : fallback,
    tierFragment(lookups, asRecord(d)?.tier_id),
  ]);
};

const legacyConsent: Summarizer = (row, lookups) => {
  const d = row.details;
  const type = str(d, "consent_type");
  const policy = str(d, "policy_version") ?? num(d, "policy_version");
  return buildSummary(type ? translate(CONSENT_TYPE_LABELS, type) : entityLabel(row), [
    policy === null ? null : `política v${policy}`,
    row.action === "consent_revoked" ? "revogou" : "aceitou",
    refFragment(resolvePerson(lookups, row.entity_id), (t) => `titular ${t}`),
  ]);
};

const retained: Summarizer = (row) => {
  const r = RETENTION[row.action];
  if (!r) return buildSummary(entityLabel(row));
  const basis = str(row.details, "basis")?.split(" — ")[0]?.trim();
  return buildSummary(r.primary, [r.why, basis && basis.length <= 40 ? basis : null]);
};

const legalDocument: Summarizer = (row, lookups) => {
  const doc = documentTarget(lookups, row.entity_id) ?? entityLabel(row);
  return buildSummary(doc, [row.action === "legal_document_updated" ? fieldsFragment(row.details) : null]);
};

const legalDocumentTierLinked: Summarizer = (row, lookups) => {
  const d = asRecord(row.details);
  const doc = documentTarget(lookups, d?.to_document_id) ?? documentTarget(lookups, row.entity_id) ?? entityLabel(row);
  return buildSummary(doc, [tierFragment(lookups, d?.tier_id)]);
};

const legalDocumentTierMoved: Summarizer = (row, lookups) => {
  const d = asRecord(row.details);
  const doc = documentTarget(lookups, d?.to_document_id) ?? documentTarget(lookups, row.entity_id) ?? entityLabel(row);
  const from = documentTarget(lookups, d?.from_document_id);
  return buildSummary(doc, [
    tierFragment(lookups, d?.tier_id),
    from ? refFragment(from, (t) => `antes em ${t}`, () => "antes em documento removido") : null,
  ]);
};

const dataExport: Summarizer = (row, lookups) => {
  const d = row.details;
  const role = str(d, "requested_by_role");
  const failed = arr(d, "sections_failed");
  const format = str(d, "format");
  return buildSummary(personPrimary(row, lookups, row.entity_id), [
    role ? translate(EXPORT_ROLE_LABELS, role) : null,
    failed === null
      ? null
      : failed.length === 0
        ? "exportação completa"
        : plural(failed.length, "seção falhou", "seções falharam"),
    format ? `formato ${format.toUpperCase()}` : null,
  ]);
};

function byWhom(self: boolean | null): string | null {
  if (self === null) return null;
  return self ? "pelo próprio titular" : "pelo staff";
}

const dataDeletionRequested: Summarizer = (row, lookups) => {
  const d = row.details;
  const self = bool(d, "requested_by_self");
  const day = formatDay(asRecord(d)?.scheduled_at);
  const grace = num(d, "grace_days");
  return buildSummary(personPrimary(row, lookups, row.entity_id), [
    self === null ? null : `pedido ${byWhom(self)}`,
    day ? `exclusão prevista para ${day}` : null,
    grace === null ? null : `prazo de ${plural(grace, "dia", "dias")}`,
  ]);
};

const dataDeletionCanceled: Summarizer = (row, lookups) => {
  const self = bool(row.details, "canceled_by_self");
  return buildSummary(personPrimary(row, lookups, row.entity_id), [self === null ? null : `cancelado ${byWhom(self)}`]);
};

const dataDeletionForced: Summarizer = (row, lookups) =>
  buildSummary(personPrimary(row, lookups, row.entity_id, "account"), [
    bool(row.details, "bypassed_grace") === true ? "sem esperar o prazo" : null,
  ]);

const dataDeletionExecuted: Summarizer = (row, lookups) => {
  const d = row.details;
  const files = num(d, "storage_files");
  const cards = num(d, "detached_cards");
  const subs = str(d, "subscriptions");
  return buildSummary(personPrimary(row, lookups, row.entity_id, "account"), [
    files === null ? null : plural(files, "arquivo apagado", "arquivos apagados"),
    cards === null ? null : plural(cards, "cartão removido", "cartões removidos"),
    subs ? `assinaturas: ${translate(DELETION_SUBSCRIPTIONS_LABELS, subs)}` : null,
  ]);
};

const lifecycleMutation: Summarizer = (row, lookups) => {
  const d = row.details;
  const role = str(d, "new_role");
  const reason = str(d, "reason");
  const effect = str(d, "contract_start_effect");
  const newTier = asRecord(d)?.new_tier_id;
  const staffRole = staffRoleInReason(d);
  return buildSummary(personPrimary(row, lookups, row.entity_id), [
    role ? `agora ${translate(ROLE_LABELS, role)}` : null,
    isUuid(newTier) ? tierFragment(lookups, newTier) : bool(d, "tier_cleared") === true ? "sem plano" : null,
    reason ? decodeLifecycleReason(reason) : null,
    staffRole ? refFragment(resolveTarget(lookups, "staff_role", staffRole), (t) => `papel ${t}`, () => "papel removido") : null,
    effect ? CONTRACT_START_EFFECT_LABELS[effect] : null,
  ]);
};

/** "plano A → B" only when both sides are known; a pending or failed side drops the fragment (KTD9). */
function tierChangeFragment(lookups: Lookups, c: { old: unknown; new: unknown }): string | null {
  const side = (id: unknown): string | null => {
    if (id === null) return "sem plano";
    const res = resolveTarget(lookups, "tier", id);
    if (res.state === "ok") return res.text;
    if (res.state === "missing") return "plano removido";
    return null;
  };
  const before = side(c.old);
  const after = side(c.new);
  return before && after ? `plano: ${before} → ${after}` : null;
}

function blockFragment(changes: Record<string, unknown>): string | null {
  const blocked = change(changes, "blocked_at");
  const reason = change(changes, "block_reason");
  const newReason = asRecord(changes.block_reason)?.new;
  if (blocked && blocked.old === null && blocked.new !== null) {
    return typeof newReason === "string" && newReason !== ""
      ? `bloqueado (${translate(BLOCK_REASON_LABELS, newReason)})`
      : "bloqueado";
  }
  if (blocked && blocked.old !== null && blocked.new === null) return "desbloqueado";
  if (reason && reason.new !== null) {
    return `motivo do bloqueio: ${enumOrNone(BLOCK_REASON_LABELS, reason.old, "nenhum")} → ${enumOrNone(BLOCK_REASON_LABELS, reason.new, "nenhum")}`;
  }
  return null;
}

const profileSensitiveUpdate: Summarizer = (row, lookups) => {
  const changes = changesOf(row.details);
  const role = change(changes, "role");
  const status = change(changes, "status");
  const tier = change(changes, "tier_id");
  const staffRole = change(changes, "staff_role_id");
  const fragments: Fragment[] = [
    role ? `papel: ${enumOrNone(ROLE_LABELS, role.old, "nenhum")} → ${enumOrNone(ROLE_LABELS, role.new, "nenhum")}` : null,
    status
      ? `status: ${enumOrNone(STATUS_LABELS, status.old, "nenhum")} → ${enumOrNone(STATUS_LABELS, status.new, "nenhum")}`
      : null,
    tier ? tierChangeFragment(lookups, tier) : null,
    staffRole
      ? staffRole.new === null
        ? "papel de staff removido"
        : refFragment(
            resolveTarget(lookups, "staff_role", staffRole.new),
            (t) => `papel de staff: ${t}`,
            () => "papel de staff: removido",
          )
      : null,
    blockFragment(changes),
  ];
  return buildSummary(personPrimary(row, lookups, row.entity_id), fragments);
};

const profileUpdated: Summarizer = (row, lookups) => {
  return buildSummary(personPrimary(row, lookups, row.entity_id), [
    fieldsFragment(row.details),
    bool(row.details, "auth_email_synced") === true ? "e-mail de login atualizado" : null,
  ]);
};

const visibilityChange: Summarizer = (row, lookups) => {
  const d = row.details;
  const field = str(d, "field");
  const visible = bool(d, "new");
  let fragment: string | null = null;
  if (field === "in_directory") {
    fragment = visible === null ? "diretório" : visible ? "entrou no diretório" : "saiu do diretório";
  } else if (field) {
    const label = translate(VISIBILITY_FIELD_LABELS, field);
    fragment = visible === null ? label : `${label}: ${visible ? "visível" : "oculto"}`;
  }
  return buildSummary(personPrimary(row, lookups, row.entity_id), [fragment]);
};

const autoDeactivate: Summarizer = (row, lookups) => {
  const d = row.details;
  const end = formatIsoDay(asRecord(d)?.contract_end);
  const reason = str(d, "reason");
  return buildSummary(personPrimary(row, lookups, row.entity_id), [
    end ? `contrato venceu em ${end}` : reason ? translate(DEACTIVATE_REASON_LABELS, reason) : null,
  ]);
};

const profileUnblocked: Summarizer = (row, lookups) => {
  const d = row.details;
  const previous = str(d, "previous_block_reason");
  const reason = str(d, "reason");
  return buildSummary(personPrimary(row, lookups, row.entity_id), [
    previous ? `bloqueio era: ${translate(BLOCK_REASON_LABELS, previous)}` : null,
    reason ? `motivo: ${reason}` : null,
  ]);
};

/** The person the row is about is `details.user_id`; the note text is never recorded or shown. */
const aboutDetailsUser: Summarizer = (row, lookups) =>
  buildSummary(personPrimary(row, lookups, asRecord(row.details)?.user_id));

const entityWithFields: Summarizer = (row) => {
  return buildSummary(entityLabel(row), [fieldsFragment(row.details)]);
};

const entityOnly: Summarizer = (row) => buildSummary(entityLabel(row));

const affinityDeleted: Summarizer = (row, lookups) => {
  const d = asRecord(row.details);
  return buildSummary(personPrimary(row, lookups, d?.user_a), [
    refFragment(resolvePerson(lookups, d?.user_b), (t) => `com ${t}`, () => "com usuário removido"),
  ]);
};

export const summarizers: Partial<Record<string, Summarizer>> = {
  consent_ledger_granted: consentLedger,
  consent_ledger_revoked: consentLedger,
  consent_granted: legacyConsent,
  consent_revoked: legacyConsent,
  consent_ledger_retained: retained,
  terms_acceptances_retained: retained,
  feed_posts_retained: retained,
  legal_document_created: legalDocument,
  legal_document_updated: legalDocument,
  legal_document_tier_linked: legalDocumentTierLinked,
  legal_document_tier_moved: legalDocumentTierMoved,
  data_export: dataExport,
  data_deletion_requested: dataDeletionRequested,
  data_deletion_canceled: dataDeletionCanceled,
  data_deletion_staff_forced: dataDeletionForced,
  data_deletion_staff_executed: dataDeletionExecuted,
  profile_lifecycle_mutation: lifecycleMutation,
  update: profileSensitiveUpdate,
  profile_updated: profileUpdated,
  profile_visibility_change: visibilityChange,
  auto_deactivate: autoDeactivate,
  profile_unblocked: profileUnblocked,
  crm_note_created: aboutDetailsUser,
  crm_note_updated: entityWithFields,
  crm_note_deleted: entityOnly,
  member_interest_added: aboutDetailsUser,
  member_interest_removed: entityOnly,
  affinity_score_deleted: affinityDeleted,
};

// ---------------------------------------------------------------------------
// Ref collectors
// ---------------------------------------------------------------------------

const entityPerson: RefCollector = (row, sink) => sink.person(row.entity_id);

const detailsUser: RefCollector = (row, sink) => sink.person(asRecord(row.details)?.user_id);

const ledgerRefs: RefCollector = (row, sink) => {
  const d = asRecord(row.details);
  sink.target("legal_document", d?.document_id);
  sink.target("tier", d?.tier_id);
};

const documentRefs: RefCollector = (row, sink) => {
  const d = asRecord(row.details);
  sink.target("legal_document", row.entity_id);
  sink.target("legal_document", d?.to_document_id);
  sink.target("legal_document", d?.from_document_id);
  sink.target("tier", d?.tier_id);
};

const lifecycleRefs: RefCollector = (row, sink) => {
  sink.person(row.entity_id);
  sink.target("tier", asRecord(row.details)?.new_tier_id);
  sink.target("staff_role", staffRoleInReason(row.details));
};

const updateRefs: RefCollector = (row, sink: RefSink) => {
  sink.person(row.entity_id);
  const changes = changesOf(row.details);
  const tier = change(changes, "tier_id");
  if (tier) {
    sink.target("tier", tier.old);
    sink.target("tier", tier.new);
  }
  sink.target("staff_role", change(changes, "staff_role_id")?.new);
};

const affinityRefs: RefCollector = (row, sink) => {
  const d = asRecord(row.details);
  sink.person(d?.user_a);
  sink.person(d?.user_b);
};

export const refCollectors: Partial<Record<string, RefCollector>> = {
  consent_ledger_granted: ledgerRefs,
  consent_ledger_revoked: ledgerRefs,
  consent_granted: entityPerson,
  consent_revoked: entityPerson,
  legal_document_created: documentRefs,
  legal_document_updated: documentRefs,
  legal_document_tier_linked: documentRefs,
  legal_document_tier_moved: documentRefs,
  data_export: entityPerson,
  data_deletion_requested: entityPerson,
  data_deletion_canceled: entityPerson,
  data_deletion_staff_forced: entityPerson,
  data_deletion_staff_executed: entityPerson,
  profile_lifecycle_mutation: lifecycleRefs,
  update: updateRefs,
  profile_updated: entityPerson,
  profile_visibility_change: entityPerson,
  auto_deactivate: entityPerson,
  profile_unblocked: entityPerson,
  crm_note_created: detailsUser,
  member_interest_added: detailsUser,
  affinity_score_deleted: affinityRefs,
};
